import { useCallback, useState } from "react";

type LazyTableParams = {
  first: number;
  rows: number;
  page: number;
  sortField: string;
  sortOrder: 1 | -1;
};

type DataTablePageEvent = {
  first: number;
  rows: number;
  page?: number;
};

type DataTableSortEvent = {
  sortField: string | null | undefined;
  sortOrder: 0 | 1 | -1 | null | undefined;
};

export function useLazyTableParams(args: {
  initialRows?: number;
  initialSortField: string;
  initialSortOrder: 1 | -1;
  /** Initial first row index (for URL-restored page). Use (page - 1) * rows for 1-based page. */
  initialFirst?: number;
}) {
  const rows = args.initialRows ?? 10;
  const initialFirst = Math.max(0, args.initialFirst ?? 0);
  const [lazyParams, setLazyParams] = useState<LazyTableParams>({
    first: initialFirst,
    rows,
    page: Math.floor(initialFirst / rows),
    sortField: args.initialSortField,
    sortOrder: args.initialSortOrder,
  });

  const onPage = useCallback((event: DataTablePageEvent) => {
    setLazyParams((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page ?? Math.floor(event.first / event.rows),
    }));
  }, []);

  const onSort = useCallback((event: DataTableSortEvent) => {
    setLazyParams((prev) => ({
      ...prev,
      sortField: event.sortField || prev.sortField,
      sortOrder:
        event.sortOrder === 1 || event.sortOrder === -1
          ? event.sortOrder
          : prev.sortOrder,
    }));
  }, []);

  const resetToFirstPage = useCallback(() => {
    setLazyParams((prev) => ({ ...prev, first: 0, page: 0 }));
  }, []);

  return {
    lazyParams,
    setLazyParams,
    onPage,
    onSort,
    resetToFirstPage,
  };
}


